<template>
  <figure class="knowledge-search-trail" :class="{ 'reduce-motion': reducedMotion }">
    <svg viewBox="0 0 360 160" aria-hidden="true" focusable="false">
      <g
        v-for="branch in branches"
        :key="branch.id"
        class="trail-branch"
        :class="{ found: foundSources.includes(branch.id), active: activeId === branch.id }"
      >
        <path class="branch-line" :d="branch.path" />
        <path class="branch-found-line" :d="branch.path" />
        <circle
          v-if="activeId === branch.id && !reducedMotion"
          class="branch-pulse"
          r="3"
          :style="{ offsetPath: `path('${branch.path}')` }"
        />

        <g :transform="`translate(192 ${branch.y - 18})`">
          <rect class="trail-source-paper" x="0.5" y="0.5" width="157" height="35" rx="7" />
          <rect class="source-found-paper" x="0.5" y="0.5" width="157" height="35" rx="7" />
          <g class="source-icon" transform="translate(11 10)">
            <path d="M 1 0.75 H 8 L 12 4.75 V 15.25 H 1 Z M 8 0.75 V 4.75 H 12" />
            <path d="M 4 8 H 9 M 4 11 H 8" />
          </g>
          <text class="source-label" x="33" y="22">{{ branch.label }}</text>
          <g class="source-check" transform="translate(142 18)">
            <circle r="6.5" />
            <path d="M -2.6 0 L -0.7 1.9 L 2.8 -2" />
          </g>
        </g>
      </g>

      <g class="source-hub" :class="{ searching: activeId }">
        <circle class="hub-ring" cx="48" cy="80" r="25" />
        <circle class="hub-paper" cx="48" cy="80" r="20" />
        <g class="hub-icon">
          <circle cx="46" cy="78" r="6" />
          <path d="M 50.5 82.5 L 56 88 M 43 76 H 47 M 43 79 H 45" />
        </g>
        <text class="hub-label" x="48" y="122" text-anchor="middle">Documentation</text>
      </g>
    </svg>
    <figcaption class="trail-description">{{ accessibleDescription }}</figcaption>
  </figure>
</template>

<script>
export default {
  name: 'KnowledgeSearchTrail',
  props: {
    foundSources: { type: Array, default: () => [] },
    stage: { type: String, default: 'clustering' },
    reducedMotion: { type: Boolean, default: false },
  },
  data() {
    return {
      branches: [
        { id: 'certificates', label: 'Renewal', y: 30, path: 'M 69 80 H 102 C 133 80 129 30 160 30 H 192' },
        { id: 'bindings', label: 'Domain binding', y: 80, path: 'M 69 80 H 192' },
        { id: 'certificate-code', label: 'Application code', y: 130, path: 'M 69 80 H 102 C 133 80 129 130 160 130 H 192' },
      ],
    };
  },
  computed: {
    activeId() {
      if (this.stage !== 'researching') return '';
      return this.branches.find((branch) => !this.foundSources.includes(branch.id))?.id || '';
    },
    accessibleDescription() {
      const found = this.branches.filter((branch) => this.foundSources.includes(branch.id));
      const active = this.branches.find((branch) => branch.id === this.activeId);
      const progress = found.length
        ? `Found documentation for ${found.map((branch) => branch.label.toLowerCase()).join(', ')}.`
        : 'Documentation branches for renewal, domain binding, and application code.';
      return active ? `${progress} Looking for ${active.label.toLowerCase()}.` : progress;
    },
  },
};
</script>

<style scoped>
.knowledge-search-trail {
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
  font-family: inherit;
}
.knowledge-search-trail svg {
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
}
.branch-line,
.branch-found-line {
  fill: none;
  stroke: var(--lab-line);
  stroke-width: 1.2;
}
.branch-found-line {
  stroke: var(--lab-success);
  opacity: 0;
  transition: opacity 200ms ease;
}
.found .branch-found-line { opacity: 0.5; }
.active .branch-line { stroke: var(--lab-accent); opacity: 0.45; }
.branch-pulse {
  fill: var(--lab-accent);
  offset-rotate: 0deg;
  animation: search-trail-pulse 1600ms linear infinite;
}
.trail-source-paper {
  fill: var(--lab-paper);
  stroke: var(--lab-line);
}
.source-found-paper {
  fill: var(--lab-success-soft);
  stroke: var(--lab-success);
  stroke-opacity: 0.38;
  opacity: 0;
  transition: opacity 220ms ease;
}
.found .source-found-paper { opacity: 1; }
.source-icon {
  fill: none;
  stroke: var(--lab-muted);
  stroke-width: 1.1;
  stroke-linejoin: round;
  stroke-linecap: round;
  opacity: 0.65;
}
.found .source-icon { stroke: var(--lab-success); opacity: 1; }
.source-label {
  fill: var(--lab-muted);
  font: 12px "Quattrocento Sans", sans-serif;
}
.active .source-label,
.found .source-label { fill: var(--lab-ink); }
.source-check {
  opacity: 0;
  transition: opacity 180ms ease;
}
.found .source-check { opacity: 1; }
.source-check circle { fill: var(--lab-success); }
.source-check path {
  fill: none;
  stroke: var(--lab-paper);
  stroke-width: 1.3;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.hub-ring { fill: var(--lab-surface); }
.hub-paper { fill: var(--lab-paper); stroke: var(--lab-line); }
.hub-icon {
  fill: none;
  stroke: var(--lab-muted);
  stroke-width: 1.4;
  stroke-linecap: round;
}
.searching .hub-icon { stroke: var(--lab-accent); }
.hub-label { fill: var(--lab-muted); font: 11px "Quattrocento Sans", sans-serif; }
.trail-description {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
@keyframes search-trail-pulse {
  0% { offset-distance: 0%; opacity: 0; }
  12% { opacity: 0.8; }
  78% { opacity: 0.8; }
  100% { offset-distance: 100%; opacity: 0; }
}
.reduce-motion .branch-found-line,
.reduce-motion .source-found-paper,
.reduce-motion .source-check { transition: none; }
@media (prefers-reduced-motion: reduce) {
  .branch-pulse { animation: none; display: none; }
  .branch-found-line,
  .source-found-paper,
  .source-check { transition: none; }
}
</style>
